import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { CompactAlert } from '~/components/common/CompactAlert'
import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'

/**
 * 非表示状態を覚える localStorage key。
 *
 * AC-IJM-001a は「非表示状態はブラウザ単位で記憶し、 サーバー保存は行わない」
 * と「ページ再訪時は再表示する」を同時に書いており両立しない (issue #6255 で
 * PdM 確認中)。 同 AC が実装手段として `localStorage` を明示していること、
 * 機能詳細も「押下時はブラウザ単位で非表示状態を記憶」としていることから、
 * **記憶する側**を採る (= #6255 の推奨案)。 サーバーには一切送らないので、
 * どちらの解釈でも共通して真な性質 (TC-IJM-B-02) は満たす。
 */
const DISMISSED_KEY = 'ebalance.adminJepxMemberInfo.bannerDismissed'

/**
 * 画面上部の説明バナー (AC-IJM-001 / AC-IJM-001a)。
 *
 * 「この画面の情報はシステム管理会社 (ベンダー) が設定する」 ことと、
 * 「発注主体別の受渡契約コードは入札時に自動で決まる」 ことを spec AC-IJM-001 の
 * 2 文で伝える。 spec の「広域マスタ」は 10/16 の画面名変更 (cf50fc9ede) の扱いに
 * そろえて「広域機関の登録内容」 と書く。
 *
 * ページ最上部の Compact info (UI 辞書 `components/alert-placement.md` §0)。1 文目を `title`、 2 文目を
 * `description`、 閉じる (×) を右端の導線に置く。 × は proto `.ecl-alert-dismiss` と同じ共通部品。
 * [Des 判断 2026-10-26] プロトタイプ (Vercel) は常設の説明として × なしの本文にしているが、 spec
 * AC-IJM-001a (P0') が閉じる操作と記憶を定めているため、 dev は × を持つ Compact にする。
 */
export const JepxMemberInfoBanner = (): React.ReactElement | null => {
  const { t } = useTranslation()
  const [isDismissed, setIsDismissed] = useState(
    () => getLocalStorageItem(DISMISSED_KEY) === 'true',
  )

  if (isDismissed) {
    return null
  }

  return (
    <CompactAlert
      actions={
        <AlertDismissButton
          onClick={() => {
            setLocalStorageItem(DISMISSED_KEY, 'true')
            setIsDismissed(true)
          }}
        />
      }
      description={t('page:adminJepxMemberInfo.banner.deliveryContractNote')}
      status="info"
      title={t('page:adminJepxMemberInfo.banner.body')}
    />
  )
}
